import type { SyncGuide } from '../guides/types.no-deps'
import type { NominalUUID, Nullable } from '../../lib/typeHelpers.no-deps'
import type { Embedding, RelevantEmbedding } from '../embeddings/types.no-deps'
import type { DatabaseReport } from '../reports/types.no-deps'
import type { FileId, FileMeta } from '../files'
import type { IngestEntityError } from '../../lib/aws/lambda/ingestEntity.no-deps'
import type { CreditBalance } from '../credits/types.no-deps'
import type { KnownUnit } from '../units/types.no-deps'
import type { GeneratedAnalysis } from './generatedAnalysis'
import type { GeneratedFilter } from './generatedFilter'

export type AiChatConfig = {
    debugInfo: boolean
    translateSearch: boolean
    sourceLimit: number
    contextFiltersWeight: number
    similarityThreshold: number
    includeTextSearchResults?: boolean
    generateFilter?: boolean
    generateAnalysis?: boolean
    windowSize: number
    hyperparams: {
        topP?: number
        topK?: number
        temperature?: number
        maxNewTokens?: number
    }
}

export type ChatId = NominalUUID<'AiChat'>
export type ChatPromptId = NominalUUID<'AiChatPrompt'>
export type ChatFileId = NominalUUID<'AiChatFileId'>

export type AiChatPredefinedAnswerCode = 'out-of-scope' | 'unknown'

export interface AiChat {
    id: ChatId
    user: number
    createdAt: Date
}

export interface AiChatPrompt {
    id: ChatPromptId
    aiChatId: ChatId
    userInput: string
    answer: string
    userContext?: AiChatUserContext
    userFeedback?: AiChatUserFeedback
    promptContext?: string
    predefinedAnswer?: AiChatPredefinedAnswerCode
    createdAt: Date
}

export type AiChatFileIngestionStatus = 'processing' | 'success' | 'error'

export interface AiChatFile {
    id: ChatFileId
    aiChatId: ChatId
    file: FileId
    status: AiChatFileIngestionStatus
    errorCode: Nullable<IngestEntityError['code']>
    createdAt: Date
}

export type AiChatDebugInfo = {
    userInput: string
    inputLangs: string[]
    normalizedUserInput: string
    generatedFilter: GeneratedFilter
    generatedAnalysis: GeneratedAnalysis
    promptContext: string
    embeddings: Array<RelevantEmbedding<Embedding>>
    config: AiChatConfig
    answer: string
}

export type AiChatResponseChunk =
    | LlmAnswerChunk
    | SourcesChunk
    | PromptIdChunk
    | PredefinedAnswerChunk
    | DebugInfoChunk
    | CreditBalanceChunk
    | ErrorChunk

interface PromptIdChunk {
    type: 'prompt-id'
    data: ChatPromptId
}

interface LlmAnswerChunk {
    type: 'llm-answer'
    data: string
}

interface SourcesChunk {
    type: 'sources'
    data: AiChatSource[]
}

interface PredefinedAnswerChunk {
    type: 'predefined-answer'
    data: string
}

interface DebugInfoChunk {
    type: 'debug-info'
    data: AiChatDebugInfo
}

interface CreditBalanceChunk {
    type: 'credit-balance'
    data: CreditBalance
}

interface ErrorChunk {
    type: 'error'
}

export interface AiChatUserContext {
    units?: number[]
    procedures?: number[]
    reportClientId?: string
    guides?: string[]
    chatFiles?: string[]
}

export interface AiChatUserFeedback {
    isGood: boolean
    note: string
}

type AiChatSourceType = 'guide' | 'report' | 'chatFile'

interface AiChatSourceBase<T extends AiChatSourceType> {
    type: T
    rank: number
    entityId?: string | number
}

export interface AiChatGuideSourcePage {
    label?: Nullable<string | number>
    number: number
}

export interface AiChatGuideSource extends AiChatSourceBase<'guide'> {
    guide: SyncGuide
    units: KnownUnit[]
    pages?: AiChatGuideSourcePage[]
}

export interface AiChatFileSource extends AiChatSourceBase<'chatFile'> {
    file: FileMeta
    pages?: AiChatGuideSourcePage[]
}

export interface AiChatReportSource extends AiChatSourceBase<'report'> {
    report: Pick<DatabaseReport, 'title' | 'id' | 'workflow' | 'submittedDate' | 'clientId' | 'campaign'> & {
        origin?: number
    }
}

export type AiChatSource = AiChatGuideSource | AiChatReportSource | AiChatFileSource

export function isAiChatGuideSource(source: AiChatSource): source is AiChatGuideSource {
    return source.type === 'guide'
}

export function isAiChatReportSource(source: AiChatSource): source is AiChatReportSource {
    return source.type === 'report'
}

export function isAiChatFileSource(source: AiChatSource): source is AiChatFileSource {
    return source.type === 'chatFile'
}
